/**
 * Store Pinia des événements d'échantillonnage (Composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { ISamplingEvent } from "../models/samplingEvent";
import { ISamplingEventApiResponse } from "../models/api/samplingEventApiResponse";
import SamplingEventServices from "../services/SamplingEventService";
import { ISamplingEventFilter } from "../models/filters/samplingEventFilter";
import { ISamplingEventForm } from "../models/forms/samplingEventForm";
import { useParameterStore } from "./parameter.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";

/**
 * Store Pinia des événements d'échantillonnage utilisant la Composition API
 */
export const useSamplingEvent = defineStore("samplingEvents", () => {
    // State
    const datas = ref<ISamplingEvent[]>([]);
    const current = ref<ISamplingEvent>({} as ISamplingEvent);
    const itemForm = ref<ISamplingEventForm>({} as ISamplingEventForm);
    const isLoading = ref(false);

    // Actions - Hydration
    /**
     * Hydrate SamplingEvents from API response
     */
    async function hydrateSamplingEvents(events: ISamplingEventApiResponse[]): Promise<ISamplingEvent[]> {
        if (events.length === 0) return [];

        // 1. Get parameter store
        const parameterStore = useParameterStore();

        // 2. Load parameters if needed
        if (parameterStore.datas.length === 0) {
            await parameterStore.load({ page: 1, pageSize: 0 });
        }

        // 3. Create Map for O(1) lookups
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: ISamplingEvent[] = [];
        for (const event of events) {
            const model = { ...event } as unknown as ISamplingEvent;

            // Link typeEvent reference (O(1) via Map)
            model.typeEvent = parameterMap.get(event.typeEventId) ?? model.typeEvent;

            models.push(model);
        }

        return models;
    }

    // Getters
    /**
     * Récupère un événement de collecte dans le store
     */
    const getSamplingEventById = computed(() => (id: number): ISamplingEvent | undefined => {
        return datas.value.find((event) => event.id == id);
    });

    /**
     * Récupère les événements par type
     */
    const getSamplingEventsByType = computed(() => (type: string): ISamplingEvent[] => {
        return datas.value.filter((event) => event.typeEvent?.code3 == type);
    });

    // Actions
    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: ISamplingEventFilter) {
        const result = await SamplingEventServices.getAll<ISamplingEventFilter, ISamplingEventApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateSamplingEvents(result.data as ISamplingEventApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un événement par son identifiant
     * @param id
     */
    async function get(id: number) {
        const result = await SamplingEventServices.getById<ISamplingEventApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateSamplingEvents([result.data as ISamplingEventApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) {
                datas.value[index] = modelData;
            }

            current.value = modelData;
            itemForm.value = await toForm(modelData);
        } else {
            console.log("Erreur lors de la récupération de l'événement " + JSON.stringify(result));
        }
        return result;
    }

    /**
     * Récupère l'item directement dans le store (sans appel API), utilisé à l'ouverture de la modale
     * puisque la liste est déjà chargée en intégralité par storeLoader.
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = await toForm(current.value);
        return current.value;
    }

    /**
     * Sauvegarde (création ou modification) de l'événement de collecte courant (itemForm).
     * La modification est synchronisée vers les autres centres via l'event bus (back).
     */
    async function save(maj: boolean = true) {
        let result = {} as ServiceOperationResult<ISamplingEvent | IDataOperationResult<ISamplingEvent>>;
        if (itemForm.value.id > 0) {
            result = await SamplingEventServices.update<ISamplingEventForm, ISamplingEventApiResponse>(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateSamplingEvents([result.data as ISamplingEventApiResponse]);
                current.value = modelData;
                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    }
                }
            }
        } else {
            result = await SamplingEventServices.create<ISamplingEventForm, ISamplingEventApiResponse>(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateSamplingEvents([result.data as ISamplingEventApiResponse]);
                current.value = modelData;
                if (maj) {
                    datas.value.push(current.value);
                }
            }
        }
        return result;
    }

    async function toForm(item: ISamplingEvent): Promise<ISamplingEventForm> {
        const form = {} as ISamplingEventForm;
        Object.assign(form, item);
        return form;
    }

    function newForm(lienId?: number): ISamplingEventForm {
        itemForm.value = {
            id: 0,
            eventName: "",
            eventNameShort: "",
            typeEventId: 0
        } as ISamplingEventForm;
        return itemForm.value;
    }

    function newItem(): ISamplingEvent {
        current.value = {
            id: 0,
            eventName: "",
            eventNameShort: "",
            typeEventId: 0
        } as ISamplingEvent;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Getters
        getSamplingEventById,
        getSamplingEventsByType,
        // Actions
        load,
        get,
        searchStoreById,
        save,
        toForm,
        newForm,
        newItem,
        hydrateSamplingEvents
    };
});

signalRManager.connection.on("MajSamplingEvent", async (payload: IPayloadStore) => {
    const store = useSamplingEvent();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateSamplingEvents([payload.data as ISamplingEventApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateSamplingEvents([payload.data as ISamplingEventApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});